import React, { useState, useMemo } from "react";

import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { formatShopifyFieldValue, type FormattedValue } from "../../../utils/shopifyFormatting";

import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";
import { getMetaTranslation } from "@/lib/views/labels/translations";

/**
 * Helper to render FormattedValue (handles both string and HTML content)
 */
function renderFormattedValue(value: FormattedValue): React.ReactNode {
  if (typeof value === "object" && "__html" in value) {
    return <span dangerouslySetInnerHTML={value} />;
  }
  return <>{value}</>;
}

/**
 * Format currency value for display
 */
function formatCurrencyValue(value: number, currency: string): string {
  const currencySymbols: Record<string, string> = {
    'THB': '฿',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
  };
  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Totals Section Widget
 * 
 * Displays order totals with special emphasis on grand total.
 * Uses gradient background and special layout for totals.
 */
export function TotalsSection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  isSaving,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);

  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  const allFields = useMemo(
    () => section.columns.flatMap((col) => col.fields),
    [section.columns]
  );

  const { visibleFieldNames } = useFieldDependencies(
    allFields,
    formData
  );

  if (section.columns.length === 0 || allFields.length === 0) {
    return null;
  }

  const fieldsToRender = allFields.filter((field) =>
    visibleFieldNames.has(field.fieldname)
  ).filter((field) => field.fieldname !== 'name');

  if (fieldsToRender.length === 0) {
    return null;
  }

  const currency = formData.currency || 'THB';

  // Check if there are any discounts applied
  // In ERPNext, discount_amount is CALCULATED from additional_discount_percentage
  // They are the SAME discount, NOT two separate discounts
  // discount_amount = (additional_discount_percentage / 100) * base_amount
  // So we should only display ONE of them, not add them together
  const additionalDiscountPercentage = parseFloat(formData.additional_discount_percentage) || 0;
  // discount_amount is the SERVER-CALCULATED total discount (single source of truth)
  const discountAmount = parseFloat(formData.discount_amount) || 0;
  const hasDiscount = discountAmount > 0;

  // Coupon code (may be present even when discount_amount is 0 — item-level discounts)
  const couponCode = formData.coupon_code as string | undefined;

  // Compute item-level discount total (from Pricing Rules / coupons that apply per-item)
  const items = Array.isArray(formData.items) ? formData.items as Array<{
    discount_percentage?: number;
    price_list_rate?: number;
    qty?: number;
    discount_amount?: number;
  }> : [];
  const itemLevelDiscountTotal = items.reduce((sum, item) => {
    const pct = parseFloat(String(item.discount_percentage)) || 0;
    const plr = parseFloat(String(item.price_list_rate)) || 0;
    const qty = parseFloat(String(item.qty)) || 1;
    const amt = parseFloat(String(item.discount_amount)) || 0;
    if (pct > 0 && plr > 0) return sum + (plr * pct / 100) * qty;
    if (amt > 0) return sum + amt * qty;
    return sum;
  }, 0);
  const hasItemLevelDiscount = itemLevelDiscountTotal > 0;

  // Get translated labels for discount fields
  const getDiscountLabel = (key: string, fallback: string) => {
    return getMetaTranslation('Sales Order', key, language) || fallback;
  };

  // Separate fields into before and after grand total
  // When individual tax rows are available, skip the aggregate total_taxes_and_charges
  const taxRows = Array.isArray(formData.taxes) ? formData.taxes as Array<{
    idx: number;
    description?: string;
    tax_amount: number;
    charge_type?: string;
    rate?: number;
  }> : [];
  const hasItemizedTaxes = taxRows.length > 0;

  const beforeGrandTotal = fieldsToRender.filter(f =>
    !['grand_total', 'rounded_total', 'in_words'].includes(f.fieldname) &&
    // Skip aggregate total_taxes_and_charges when we have itemized rows
    !(hasItemizedTaxes && f.fieldname === 'total_taxes_and_charges')
  );
  const grandTotalField = fieldsToRender.find(f => f.fieldname === 'grand_total');
  const roundedTotalField = fieldsToRender.find(f => f.fieldname === 'rounded_total');
  const inWordsField = fieldsToRender.find(f => f.fieldname === 'in_words');

  return (
    <>
      <div className="space-y-2">
        <div
          className={cn(
            "rounded-xl border border-border bg-card overflow-clip",
            !disabled && "cursor-pointer",
            disabled && "cursor-not-allowed"
          )}
          onClick={() => !disabled && setModalOpen(true)}
        >
          <div className="p-6">
            <div className="space-y-3">
              {/* Regular fields (net_total, taxes, etc.) */}
              {beforeGrandTotal.map((field, index) => {
                const value = formData[field.fieldname];
                const displayValue = formatShopifyFieldValue(field, value, currency);
                const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
                const isCurrency = field.fieldtype === 'Currency' ||
                  ['amount', 'total', 'net_total', 'total_taxes_and_charges'].includes(field.fieldname);
                const isPercentage = field.fieldname?.includes('percentage') ||
                  field.fieldtype === 'Percent';

                return (
                  <div
                    key={field.fieldname}
                    className={cn(
                      "flex items-center justify-between",
                      index < beforeGrandTotal.length - 1 && "pb-2"
                    )}
                  >
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {resolvedLabel.label}
                    </div>
                    <div className={cn(
                      "text-sm font-medium",
                      (isCurrency || isPercentage) && "font-semibold",
                      "text-gray-900 dark:text-gray-100"
                    )}>
                      {renderFormattedValue(displayValue)}
                    </div>
                  </div>
                );
              })}

              {/* Itemized tax/charge rows (VAT, Shipping, etc.) */}
              {hasItemizedTaxes && taxRows.map((row) => (
                <div
                  key={`tax-${row.idx}`}
                  className="flex items-center justify-between pb-2"
                >
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {row.description || `Tax ${row.idx}`}
                    {row.charge_type !== 'Actual' && row.rate ? (
                      <span className="text-gray-400 ml-1">({row.rate}%)</span>
                    ) : null}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrencyValue(row.tax_amount, currency)}
                  </div>
                </div>
              ))}

              {/* Discount Section - show if any discount is applied */}
              {/* discount_amount is the SERVER-CALCULATED total discount (single source of truth) */}
              {hasDiscount && (
                <div className="border-t border-border pt-3 mt-1">
                  <div className="flex items-center justify-between pb-2">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {getDiscountLabel('discount_amount', 'Discount')}
                      {additionalDiscountPercentage > 0 && (
                        <span className="text-gray-400 ml-1">
                          ({additionalDiscountPercentage.toFixed(2)}%)
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-red-600 dark:text-red-400">
                      -{formatCurrencyValue(discountAmount, currency)}
                    </div>
                  </div>
                </div>
              )}

              {/* Coupon + item-level discount (from Pricing Rules) */}
              {(couponCode || hasItemLevelDiscount) && (
                <div className={cn(
                  "pt-3 mt-1",
                  !hasDiscount && "border-t border-border"
                )}>
                  {couponCode && (
                    <div className="flex items-center justify-between pb-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        คูปอง
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="currentColor"><path d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1.5a.5.5 0 0 1-.5.5 1.5 1.5 0 0 0 0 3 .5.5 0 0 1 .5.5V11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9.5a.5.5 0 0 1 .5-.5 1.5 1.5 0 0 0 0-3A.5.5 0 0 1 2 5.5V4z"/></svg>
                        {couponCode}
                      </span>
                    </div>
                  )}
                  {hasItemLevelDiscount && !hasDiscount && (
                    <div className="flex items-center justify-between pb-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        ส่วนลด
                      </div>
                      <div className="text-sm font-semibold text-red-600 dark:text-red-400">
                        -{formatCurrencyValue(itemLevelDiscountTotal, currency)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Grand Total - prominent display */}
              {grandTotalField && (
                <div className={cn("pt-3", (hasDiscount || couponCode || hasItemLevelDiscount) ? "" : "border-t border-border")}>
                  <div className="flex items-center justify-between">
                    <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      {resolveLabel(doctype, grandTotalField, mode, style, enableSystemTranslations).label}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {renderFormattedValue(formatShopifyFieldValue(grandTotalField, formData[grandTotalField.fieldname], currency))}
                    </div>
                  </div>
                </div>
              )}

              {/* Rounded Total */}
              {roundedTotalField && formData[roundedTotalField.fieldname] && (
                <div className="flex items-center justify-between pt-1">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {resolveLabel(doctype, roundedTotalField, mode, style, enableSystemTranslations).label}
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {renderFormattedValue(formatShopifyFieldValue(roundedTotalField, formData[roundedTotalField.fieldname], currency))}
                  </div>
                </div>
              )}

              {/* In Words */}
              {inWordsField && formData[inWordsField.fieldname] && (
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {resolveLabel(doctype, inWordsField, mode, style, enableSystemTranslations).label || 'In Words'}
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 italic">
                    {renderFormattedValue(formatShopifyFieldValue(inWordsField, formData[inWordsField.fieldname], currency))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
      />
    </>
  );
}

